import React from 'react';
import { useTranslation } from 'react-i18next';

export const BuyPage: React.FC = () => {
  const { t } = useTranslation();

  const buyingSteps = [
    {
      step: 1,
      icon: 'fas fa-user-plus',
      duration: '5-10 min'
    },
    {
      step: 2,
      icon: 'fas fa-wallet',
      duration: '2-5 min'
    },
    {
      step: 3,
      icon: 'fas fa-search',
      duration: '1 min'
    },
    {
      step: 4,
      icon: 'fas fa-shopping-cart',
      duration: 'Instant'
    }
  ];

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="floating-element"></div>
        <div className="floating-element"></div>
        <div className="floating-element"></div>
      </div>

      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="hero-background absolute inset-0">
          <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='grid' width='100' height='100' patternUnits='userSpaceOnUse'%3E%3Cpath d='M 100 0 L 0 0 0 100' fill='none' stroke='rgba(212, 175, 55, 0.1)' stroke-width='1'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23grid)' /%3E%3C/svg%3E")`,
            }}
          />
        </div>

        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
          <div className="hero-badge inline-block px-4 py-2 bg-gold/10 rounded-full mb-6">
            <span className="text-gold font-semibold text-sm uppercase tracking-wider">{t('buy.title')}</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-black text-white mb-8 leading-tight bg-gradient-to-r from-gold to-gold-light bg-clip-text text-transparent">
            {t('buy.title')}
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            {t('buy.howToBuy.description')}
          </p>
        </div>
      </section>

      {/* CEX Information */}
      <section className="section relative bg-bg-secondary/50">
        <div className="container">
          <div className="text-center mb-20">
            <div className="hero-badge inline-block px-4 py-2 bg-gold/10 rounded-full mb-6">
              <span className="text-gold font-semibold text-sm uppercase tracking-wider">{t('buy.exchange.badge')}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
              {t('buy.exchange.title')}
            </h2>
            <p className="text-xl text-gray-400 max-w-4xl mx-auto leading-relaxed">
              {t('buy.exchange.description')}
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            {/* DEX Information */}
            <div className="cta-section relative border-2 border-gold/30 mb-8">
              <div className="text-center">
                <div className="w-24 h-24 bg-gradient-to-br from-gold/20 to-gold/5 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <i className="fas fa-chart-line text-5xl text-gold"></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">Buy on Centralized Exchanges (CEX)</h3>
                <p className="text-gray-300 mb-6">
                  IDNG is available on top centralized exchanges. You can easily trade IDNG with USDT.
                </p>

                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <a
                    href="https://www.bifinance.com/trade?symbol=IDNG/USDT"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-gold to-gold-dark text-black font-bold rounded-lg hover:shadow-lg hover:shadow-gold/20 transition-all transform hover:-translate-y-1 w-full sm:w-auto justify-center"
                  >
                    <span>Trade on BiFinance</span>
                    <i className="fas fa-external-link-alt"></i>
                  </a>

                  <a
                    href="https://www.coinstore.com/spot/IDNGUSDT"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-bold rounded-lg hover:shadow-lg hover:shadow-cyan-500/20 transition-all transform hover:-translate-y-1 w-full sm:w-auto justify-center"
                  >
                    <span>Trade on Coinstore</span>
                    <i className="fas fa-external-link-alt"></i>
                  </a>
                </div>
              </div>
            </div>

            <div className="cta-section relative border-2 border-gold/30">
              <div className="text-center">
                <div className="w-24 h-24 bg-gradient-to-br from-gold/20 to-gold/5 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <i className="fas fa-shield-check text-5xl text-gold"></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">{t('buy.exchange.subtitle')}</h3>
                <p className="text-gray-300 mb-8">
                  {t('buy.exchange.description')}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 text-left">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gold/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="fas fa-check text-gold text-xl"></i>
                    </div>
                    <div>
                      <div className="text-white font-semibold mb-1">{t('buy.exchange.features.feature1')}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gold/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="fas fa-check text-gold text-xl"></i>
                    </div>
                    <div>
                      <div className="text-white font-semibold mb-1">{t('buy.exchange.features.feature2')}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gold/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="fas fa-check text-gold text-xl"></i>
                    </div>
                    <div>
                      <div className="text-white font-semibold mb-1">{t('buy.exchange.features.feature3')}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gold/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="fas fa-check text-gold text-xl"></i>
                    </div>
                    <div>
                      <div className="text-white font-semibold mb-1">{t('buy.exchange.features.feature4')}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Buying Guide */}
      <section className="section relative">
        <div className="container">
          <div className="text-center mb-20">
            <div className="hero-badge inline-block px-4 py-2 bg-gold/10 rounded-full mb-6">
              <span className="text-gold font-semibold text-sm uppercase tracking-wider">How to Buy</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">{t('buy.steps.title')}</h2>
            <p className="text-xl text-gray-400 max-w-4xl mx-auto leading-relaxed">
              {t('buy.steps.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {buyingSteps.map((step, index) => (
              <div key={index} className="step-card modern-card text-center group">
                <div className="step-number mb-6 group-hover:scale-110 transition-transform">
                  {step.step}
                </div>
                <div className="w-16 h-16 bg-gradient-to-br from-gold/20 to-gold/5 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                  <i className={`${step.icon} text-3xl text-gold`}></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-4 group-hover:text-gold transition-colors">
                  {t(`buy.steps.step${step.step}.title`)}
                </h3>
                <p className="text-gray-400 leading-relaxed mb-4">
                  {t(`buy.steps.step${step.step}.description`)}
                </p>
                <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                  <i className="fas fa-clock"></i>
                  <span>{step.duration}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>




    </div>
  );
};